import { useState, MouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import { Badge, Box, Button, Chip, Divider, FormControlLabel, IconButton, Link, Popover, Stack, Switch, Tooltip, Typography } from '@mui/material'
import NotificationsIcon from '@mui/icons-material/Notifications'
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'

import { useToggleNotifications } from '../model/useToggleNotifications'

// Opens the macOS Notifications pane (Ventura/Sonoma/Sequoia). From a browser this is an external
// scheme: the OS shows an "Open System Settings?" confirmation and may land on the pane top-level
// rather than the app's row — so the written steps remain the source of truth.
const MACOS_NOTIF_SETTINGS_URL = 'x-apple.systempreferences:com.apple.Notifications-Settings.extension'

function getBrowserName(): string {
  const ua = navigator.userAgent
  if (/Edg\//.test(ua)) return 'Microsoft Edge'
  if ((navigator as unknown as { brave?: unknown }).brave) return 'Brave'
  if (/OPR\//.test(ua)) return 'Opera'
  if (/Chrome\//.test(ua)) return 'Google Chrome'
  if (/Firefox\//.test(ua)) return 'Firefox'
  if (/Safari\//.test(ua)) return 'Safari'
  return 'your browser'
}

function isMacOS(): boolean {
  return /Mac/i.test(navigator.platform) || /Mac OS X/i.test(navigator.userAgent)
}

type View = 'main' | 'verify' | 'troubleshoot'

export function NotificationSetup({ waitingRoomId }: { waitingRoomId: string }) {
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const { notificationsEnabled, toggleNotifications, permission, verified, sendTestNotification, confirmVerified } =
    useToggleNotifications(waitingRoomId)

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [view, setView] = useState<View>('main')

  const supported = permission !== 'unsupported'
  const open = Boolean(anchorEl)
  const tk = (key: string, options?: Record<string, unknown>) => t(`page.waitingRoom.notification.${key}`, options)

  const openPopover = (e: MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget)
    setView(permission === 'denied' ? 'troubleshoot' : 'main')
  }

  const runTest = async () => {
    const created = await sendTestNotification()
    setView(created ? 'verify' : 'troubleshoot')
  }

  const onSawIt = () => {
    confirmVerified(true)
    enqueueSnackbar(tk('test.success'), { variant: 'success' })
    setView('main')
    setAnchorEl(null)
  }

  const statusChip = () => {
    if (permission === 'denied') return <Chip size="small" color="error" label={tk('status.blocked')} />
    if (notificationsEnabled && verified) return <Chip size="small" color="success" label={tk('status.on')} />
    return <Chip size="small" variant="outlined" label={tk('status.notSetUp')} />
  }

  // Warn (dot) when the doctor enabled notifications but never confirmed they actually arrive.
  const showWarningDot = notificationsEnabled && !verified

  return (
    <>
      <Tooltip title={tk('setup.title')}>
        <span>
          <IconButton onClick={openPopover} color="primary" size="small" disabled={!supported} aria-label={tk('setup.title')}>
            <Badge color="warning" variant="dot" invisible={!showWarningDot}>
              {notificationsEnabled ? <NotificationsIcon /> : <NotificationsOffIcon />}
            </Badge>
          </IconButton>
        </span>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ p: 2, width: 340 }}>
          <Stack spacing={1.5}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="subtitle1">{tk('setup.title')}</Typography>
              {statusChip()}
            </Stack>

            <FormControlLabel
              control={<Switch checked={notificationsEnabled} onChange={() => toggleNotifications()} disabled={permission === 'denied'} />}
              label={<Typography variant="body2">{tk('setup.enableLabel')}</Typography>}
            />

            {showWarningDot && view === 'main' && (
              <Typography variant="caption" color="warning.main">
                {tk('badge.mayBeBlocked')}
              </Typography>
            )}

            {view === 'main' && (
              <Button variant="outlined" size="small" onClick={runTest} disabled={permission === 'denied'}>
                {tk('test.button')}
              </Button>
            )}

            {view === 'verify' && (
              <>
                <Divider />
                <Typography variant="subtitle2">{tk('test.verifyTitle')}</Typography>
                <Typography variant="body2" color="text.secondary">{tk('test.verifyBody')}</Typography>
                <Stack direction="row" spacing={1}>
                  <Button variant="contained" color="success" size="small" onClick={onSawIt}>{tk('test.confirmYes')}</Button>
                  <Button variant="text" size="small" onClick={() => { confirmVerified(false); setView('troubleshoot') }}>
                    {tk('test.confirmNo')}
                  </Button>
                </Stack>
              </>
            )}

            {view === 'troubleshoot' && (
              <>
                <Divider />
                <Typography variant="subtitle2">{tk('troubleshoot.title')}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {tk('troubleshoot.macosSteps', { browser: getBrowserName() })}
                </Typography>
                <Typography variant="caption" color="text.secondary">{tk('troubleshoot.focusHint')}</Typography>
                <Stack direction="row" spacing={1} sx={{ pt: 0.5 }}>
                  {isMacOS() && (
                    <Button component={Link} href={MACOS_NOTIF_SETTINGS_URL} variant="outlined" size="small" endIcon={<OpenInNewIcon />}>
                      {tk('troubleshoot.openSettings')}
                    </Button>
                  )}
                  <Button variant="contained" size="small" onClick={runTest}>{tk('troubleshoot.retry')}</Button>
                </Stack>
              </>
            )}
          </Stack>
        </Box>
      </Popover>
    </>
  )
}
